/** Typed client for the Go API. One place that knows about auth + error shape. */

export interface Plan {
  id: string;
  code: string;
  name: string;
  tagline: string;
  price_cents: number;
  currency: string;
  interval: string;
  rate_limit_rpm: number;
  burst_size: number;
  monthly_quota: number;
  max_horizon: number;
  max_context: number;
  max_series: number;
  max_keys: number;
  quantiles: boolean;
  covariates: boolean;
  support_level: string;
  features: string[];
  sort_order: number;
  highlighted: boolean;
  active: boolean;
  contact_sales: boolean;
}

export interface User {
  id: string;
  email: string;
  name: string;
  company: string;
  plan_code: string;
  status: string;
  created_at: string;
  billing_status?: string;
  grace_ends_at?: string;
  pending_plan_code?: string;
  is_admin?: boolean;
  email_verified: boolean;
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  last4: string;
  scopes: string[];
  revoked: boolean;
  last_used_at?: string;
  created_at: string;
  revoked_at?: string;
  /** Only present in the response that created the key. */
  key?: string;
}

export interface UsageCounter {
  period: string;
  requests: number;
  series: number;
  forecast_points: number;
  errors: number;
  throttled_count: number;
  inference_millis: number;
}

export interface DailyCount {
  day: string;
  requests: number;
  errors: number;
  avg_latency_ms: number;
}

export interface UsageResponse {
  period: string;
  period_ends_at: string;
  current: UsageCounter;
  quota: number;
  quota_used_pct: number;
  remaining: number;
  plan: Plan | null;
  history: UsageCounter[];
  daily: DailyCount[];
}

export interface RequestLog {
  id: string;
  key_prefix: string;
  route: string;
  model: string;
  status: number;
  series: number;
  horizon: number;
  latency_ms: number;
  error?: string;
  created_at: string;
}

export interface Subscription {
  id: string;
  plan_code: string;
  status: string;
  price_cents: number;
  currency: string;
  provider: string;
  provider_ref: string;
  provider_price_id?: string;
  cancel_at_period_end: boolean;
  current_period: string;
  period_start: string;
  period_end: string;
  canceled_at?: string;
  created_at: string;
}

export interface Invoice {
  id: string;
  provider_ref: string;
  number?: string;
  status: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  plan_code?: string;
  hosted_url?: string;
  pdf_url?: string;
  period_start: string;
  period_end: string;
  issued_at: string;
}

export interface AdminUserRow extends User {
  subscription: Subscription | null;
}

export interface AdminUsersResponse {
  total_users: number;
  paying_users: number;
  page: number;
  limit: number;
  total_pages: number;
  users: AdminUserRow[];
}

export interface BillingResponse {
  plan: Plan | null;
  subscription: Subscription | null;
  history: Subscription[];
  invoices: Invoice[];
  /** Plan the subscription switches to at the end of the current period. */
  pending_plan: Plan | null;
  billing_status: string;
  grace_ends_at?: string;
  provider: string;
  portal_available: boolean;
}

/**
 * What the server did with a plan change. `checkout_url` means nothing has been
 * applied yet and the browser must be sent to the payment page.
 */
export interface SubscribeResponse {
  checkout_url?: string;
  plan?: Plan;
  applied: 'immediate' | 'at_period_end' | null;
  effective_at?: string;
  effective_immediately?: boolean;
  subscription?: Subscription;
}

export interface SessionResponse {
  token: string;
  expires_at: string;
  user: User;
  plan: Plan | null;
}

export interface MeResponse {
  user: User;
  plan: Plan | null;
  usage: UsageCounter;
  active_keys: number;
}

export interface ForecastItem {
  index: number;
  point: number[];
  mean?: number[];
  quantiles?: Record<string, number[]>;
}

export interface ForecastResponse {
  model: string;
  horizon: number;
  forecasts: ForecastItem[];
  usage: {
    series: number;
    forecast_points: number;
    requests_this_period: number;
    monthly_quota: number;
    plan: string;
  };
  latency_ms: number;
  inference_ms: number;
  request_id: string;
  stub?: boolean;
  warning?: string;
}

/** Mirrors the server's single error envelope. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  /** Seconds the server asked us to wait, from Retry-After. */
  readonly retryAfter: number | null;

  constructor(status: number, code: string, message: string, details: unknown, retryAfter: number | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.retryAfter = retryAfter;
  }
}

const TOKEN_KEY = 'vectime.session';

export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null; // private mode / storage disabled
  }
}

export function writeToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* non-fatal: the session just won't survive a reload */
  }
}

export const BASE = import.meta.env.VITE_API_URL ?? '';

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Send an API key instead of the session JWT (the playground does this). */
  apiKey?: string;
  /** Skip auth entirely (public endpoints). */
  anonymous?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  if (opts.apiKey) {
    headers['X-API-Key'] = opts.apiKey;
  } else if (!opts.anonymous) {
    const token = readToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(BASE + path, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const envelope = parsed as { error?: { code?: string; message?: string; details?: unknown } } | null;
    const retryHeader = res.headers.get('Retry-After');
    throw new ApiError(
      res.status,
      envelope?.error?.code ?? 'unknown_error',
      envelope?.error?.message ?? `request failed with status ${res.status}`,
      envelope?.error?.details ?? null,
      retryHeader ? Number(retryHeader) : null,
    );
  }
  return parsed as T;
}

export const api = {
  // ---- public ----
  listPlans: () => request<{ plans: Plan[] }>('/api/plans', { anonymous: true }),

  register: (body: { email: string; password: string; name: string; company: string }) =>
    request<SessionResponse>('/api/auth/register', { method: 'POST', body, anonymous: true }),

  login: (body: { email: string; password: string }) =>
    request<SessionResponse>('/api/auth/login', { method: 'POST', body, anonymous: true }),

  /** Clicked from the V1 email; no session on the device that receives it. */
  verifyEmail: (uid: string, token: string) =>
    request<{ status: 'verified' | 'already_verified' }>(
      `/api/auth/verify-email?uid=${encodeURIComponent(uid)}&token=${encodeURIComponent(token)}`,
      { anonymous: true },
    ),

  /** Only fired after an explicit confirm click — see pages/CancelSignup. */
  cancelSignup: (uid: string, token: string) =>
    request<{ status: 'deleted' }>(
      `/api/auth/cancel-signup?uid=${encodeURIComponent(uid)}&token=${encodeURIComponent(token)}`,
      { method: 'POST', anonymous: true },
    ),

  // ---- dashboard ----
  me: () => request<MeResponse>('/api/me'),

  resendVerification: () =>
    request<{ status: 'sent' | 'already_verified' }>('/api/auth/resend-verification', { method: 'POST' }),

  updateProfile: (body: { name: string; company: string }) =>
    request<{ user: User }>('/api/me', { method: 'PATCH', body }),

  listKeys: () => request<{ keys: ApiKey[]; max_keys: number }>('/api/keys'),

  createKey: (body: { name: string; test: boolean }) =>
    request<{ key: ApiKey; warning: string }>('/api/keys', { method: 'POST', body }),

  revokeKey: (id: string) => request<{ revoked: boolean }>(`/api/keys/${id}`, { method: 'DELETE' }),

  usage: (days = 30) => request<UsageResponse>(`/api/usage?days=${days}`),

  recentRequests: (limit = 50) => request<{ requests: RequestLog[] }>(`/api/usage/requests?limit=${limit}`),

  billing: () => request<BillingResponse>('/api/billing'),

  subscribe: (planCode: string) =>
    request<SubscribeResponse>('/api/billing/subscribe', {
      method: 'POST',
      body: { plan_code: planCode },
    }),

  billingPortal: () => request<{ portal_url: string }>('/api/billing/portal', { method: 'POST' }),

  // ---- admin ----
  adminUsers: (page = 1, limit = 50) =>
    request<AdminUsersResponse>(`/api/admin/users?page=${page}&limit=${limit}`),

  /** Undo a plan change or cancellation that was waiting for the renewal. */
  cancelScheduledChange: () =>
    request<{ plan_code: string; renews_at: string; resumed: boolean }>('/api/billing/pending', {
      method: 'DELETE',
    }),

  // ---- metered API, called with a real key (the playground dogfoods /v1) ----
  forecast: (
    apiKey: string,
    body: { series: number[][]; horizon: number; quantiles: boolean },
    signal?: AbortSignal,
  ) => request<ForecastResponse>('/v1/forecast', { method: 'POST', body, apiKey, signal }),

  /**
   * Same metered forecast, authenticated by the dashboard session instead of a
   * key. Quota, rate limit and the request log all apply identically — it just
   * spares a logged-in user from minting a key to try one call.
   */
  playgroundForecast: (
    body: { series: number[][]; horizon: number; quantiles: boolean },
    signal?: AbortSignal,
  ) => request<ForecastResponse>('/api/playground/forecast', { method: 'POST', body, signal }),
};

export function formatPrice(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}

export function formatQuota(n: number): string {
  return n === 0 ? 'Unmetered' : formatNumber(n);
}

/**
 * Contact-sales limits are a floor, not a ceiling — the contract raises them —
 * so they render as "512+" rather than as a hard number.
 */
export function formatLimit(n: number, contactSales: boolean): string {
  return contactSales ? `${formatNumber(n)}+` : formatNumber(n);
}

export function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDateTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
